/**
 * One company, product or solution type, in full: site, who they are, what
 * they sell with features and plans, the pains found under them, tags,
 * verdicts — and what is still missing, with a Dig button. The page a person
 * opens when they click a name anywhere in the room.
 */
import { useState, type ReactNode } from 'react';
import type {
  BoardroomFilings,
  BoardroomItemLinkRow,
  BoardroomMaker,
  BoardroomMergedName,
  BoardroomPersona,
  BoardroomReceipt,
  BoardroomProfile,
  BoardroomProfileHead,
  BoardroomProfilePain,
  BoardroomProfileProduct,
  BoardroomTableDig,
} from '../../../types';
import { KIND_LABELS, kindTone } from '../kinds';
import { VerdictBadge } from '../ItemCard';
import Critiques from './Critiques';
import FeatureProfile from './FeatureProfile';
import { FRESH_CLASS, FreshIds, GLOW_BASE, useArrivals, useFreshClass } from './freshness';
import MissingList, { type OnDig } from './MissingList';
import { DemandTermsTable } from './LookupPanels';
import { Facts, Links, Voices } from './ProfileFindings';
import { RankingsTable } from './SearchMarketReport';
import ItemRefText from '../ItemRefText';
import TagChip from '../TagChip';
import WhyHere from './WhyHere';
import { linkGroups } from './itemLinks';
import QuestionAnswer from './QuestionAnswer';
import Videos from './Videos';
import SiteImages from './SiteImages';
import { feedTime, feedTimeTitle } from '../feedTime';

/** A receipt in words, from the head's compact shape. */
function receiptWords(r: BoardroomReceipt): string {
  if (r.kind === 'LOOKUP_SERP' && r.keyword) return `the search "${r.keyword}"`;
  if (r.kind === 'LOOKUP_PAGE' && r.url) return `the page ${r.url.replace(/^https?:\/\//, '')}`;
  if (r.kind === 'LOOKUP_DEMAND' && r.seeds?.length) return `Google Ads numbers for ${r.seeds.slice(0, 3).join(', ')}`;
  if (r.kind === 'LOOKUP_TRENDS' && r.keyword) return `Google Trends for "${r.keyword}"`;
  if (r.kind === 'LOOKUP_NEWS' && r.keyword) return `the news about "${r.keyword}"`;
  if (r.kind === 'LOOKUP_SOCIAL' && r.keyword) return `posts about "${r.keyword}"`;
  if (r.kind === 'LOOKUP_CONSOLE' && r.keyword) return `a console search for "${r.keyword}"`;
  if (r.kind === 'EVIDENCE') return `the evidence "${r.title}"`;
  return r.title;
}

/** How many sources the head lists before "show all". */
const SOURCES_SHOWN = 3;

function ReceiptLink({ r, onOpen }: { r: BoardroomReceipt; onOpen: (id: number) => void }) {
  return (
    <button type="button" onClick={() => onOpen(r.id)} className="text-indigo-600 dark:text-indigo-400 hover:underline text-left" title={`#${r.id}`}>
      {receiptWords(r)}
    </button>
  );
}

function Head({
  item,
  watchControl,
  onEdit,
  onCompare,
  onOpen,
  onPickTag,
  lensTags,
  personas,
  dig,
  onDig,
  digging,
  unseen,
  onSeen,
  madeBy,
}: {
  item: BoardroomProfileHead;
  /** A product's maker and what else it sells, said under the name. */
  madeBy?: { company: BoardroomMaker | null; siblings: { id: number; title: string }[] } | null;
  /** Compare this company with others, side by side. */
  onCompare?: () => void;
  /** The "watch this company" control, passed in rather than imported.
   *
   *  THIS FILE IS UNIT-TESTED, and CRA's jest cannot resolve react-router-dom v7 — which the
   *  control reaches through `ShellLink`. Importing it here made `profileView.test.tsx`
   *  unloadable. A slot keeps the router in the page that renders this and keeps this testable,
   *  the same trade `PageHeader`'s `actions` and `ReturnTrail`'s injected sections make. */
  watchControl?: ReactNode;
  onEdit: () => void;
  onOpen: (id: number) => void;
  /** A tag clicked: put it in the lens, or take it out. Without it the tags are plain text. */
  onPickTag?: (key: string) => void;
  /** The tags in the lens now, so a chip in it looks picked. */
  lensTags?: readonly string[];
  personas?: Map<number, BoardroomPersona>;
  /** Everything this item lacks, as one directive. Null when it lacks nothing. */
  dig: BoardroomTableDig | null;
  onDig: OnDig;
  digging: boolean;
  unseen?: ReadonlySet<number>;
  onSeen?: (ids: number[]) => void;
}) {
  const [allSources, setAllSources] = useState(false);
  const by = item.created_by_persona_id == null ? null : personas?.get(item.created_by_persona_id)?.name ?? `persona #${item.created_by_persona_id}`;
  return (
    <header className="shrink-0 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 p-3" data-testid="profile-head">
      <div className="flex items-baseline gap-2 flex-wrap">
        <span className={`text-xs px-1.5 py-0.5 rounded ${kindTone(item.kind)}`}>{KIND_LABELS[item.kind]}</span>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{item.title}</h2>
        {/* WHOSE PRODUCT, under its name. "Sold by" sat below the product card,
            under every feature, and a product's page read as a company's. */}
        {madeBy && (
          <span className="text-sm text-gray-600 dark:text-gray-300" data-testid="profile-made-by">
            {madeBy.company ? (
              <>
                by{' '}
                <button
                  type="button"
                  onClick={() => onOpen(madeBy.company!.id)}
                  className="font-medium text-teal-700 hover:underline dark:text-teal-300"
                >
                  {madeBy.company.title}
                </button>{' '}
                <MakerSureness maker={madeBy.company} onOpen={onOpen} />
              </>
            ) : (
              <span className="italic text-gray-400">maker not known yet</span>
            )}
          </span>
        )}
        {item.verdict && <VerdictBadge verdict={item.verdict} />}
        {item.url ? (
          <a href={item.url} target="_blank" rel="noreferrer" className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline truncate max-w-md">
            {item.url.replace(/^https?:\/\//, '')}
          </a>
        ) : (
          <span className="text-xs text-gray-400 italic">no website recorded</span>
        )}
        <span className="ml-auto flex items-center gap-2">
          {onCompare && (
            <button
              type="button"
              onClick={onCompare}
              title="See this company next to others: price, pitch, features and complaints"
              data-testid="profile-compare"
              className="text-xs px-2 py-0.5 rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Compare with…
            </button>
          )}
          <button type="button" onClick={onEdit} className="text-xs px-2 py-0.5 rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
            Edit
          </button>
        </span>
      </div>
      {madeBy?.company && madeBy.siblings.length > 0 && (
        <div className="mt-1 text-xs text-gray-500 dark:text-gray-400" data-testid="profile-siblings">
          {madeBy.company.title} also sells{' '}
          {madeBy.siblings.map((sib, i) => (
            <span key={sib.id}>
              {i > 0 && ', '}
              <button type="button" onClick={() => onOpen(sib.id)} className="hover:underline">
                {sib.title}
              </button>
            </span>
          ))}
        </div>
      )}
      {item.body ? (
        <p className="mt-1.5 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line">
          <ItemRefText text={item.body} onOpen={onOpen} />
        </p>
      ) : (
        <p className="mt-1.5 text-xs text-gray-400 italic">no description yet</p>
      )}
      {/* WHERE RESEARCH BECOMES MONITORING. Here rather than on the competitors table, which
          deliberately has no per-row buttons — sixteen of them down one column is noise, and each
          would be lying about its own scope. This is the page you are already on to read about ONE
          company, which is where a verb about one company belongs. */}
      {watchControl && (
        <div className="mt-2 border-t border-gray-100 dark:border-gray-700 pt-2">{watchControl}</div>
      )}
      {/* WHERE IT CAME FROM — the question the page did not answer. */}
      <div className="mt-1.5 text-xs text-gray-500 dark:text-gray-400" data-testid="profile-source">
        {by === null ? (
          'added by hand'
        ) : item.receipts.length > 0 ? (
          <>
            found by {by} from{item.receipts.length > 1 ? ` ${item.receipts.length} sources:` : ''}{' '}
            {item.receipts.length === 1 ? (
              <ReceiptLink r={item.receipts[0]} onOpen={onOpen} />
            ) : (
              // ONE PER LINE, the first few until asked. Run together in a
              // sentence, eleven searches and pages wrapped into a block
              // nobody could read.
              <ul className="mt-0.5 space-y-0.5 pl-3">
                {(allSources ? item.receipts : item.receipts.slice(0, SOURCES_SHOWN)).map(r => (
                  <li key={r.id} className="truncate">
                    <ReceiptLink r={r} onOpen={onOpen} />
                  </li>
                ))}
                {item.receipts.length > SOURCES_SHOWN && (
                  <li>
                    <button
                      type="button"
                      onClick={() => setAllSources(v => !v)}
                      className="text-gray-500 hover:underline dark:text-gray-400"
                      data-testid="profile-source-more"
                    >
                      {allSources ? 'show fewer' : `show all ${item.receipts.length}`}
                    </button>
                  </li>
                )}
              </ul>
            )}
          </>
        ) : (
          <span className="text-amber-700 dark:text-amber-300">found by {by} - no receipt: nothing on the board says where this came from</span>
        )}
      </div>
      {item.tags.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {item.tags.map(t => {
            const key = `${t.dimension}:${t.value}`;
            return (
              <TagChip
                key={key}
                dimension={t.dimension}
                value={t.value}
                showDimension
                picked={lensTags?.includes(key) ?? false}
                onPick={onPickTag ? () => onPickTag(key) : undefined}
                testId={onPickTag ? 'profile-tag' : undefined}
              />
            );
          })}
        </div>
      )}
    </header>
  );
}

function Pains({ pains, onOpen }: { pains: BoardroomProfilePain[]; onOpen: (id: number) => void }) {
  const glow = useFreshClass();
  if (pains.length === 0) return <span className="text-xs text-gray-400 italic">none recorded</span>;
  return (
    <ul className="flex flex-col gap-1">
      {pains.map(p => (
        <li key={p.id} className={`text-xs flex items-baseline gap-1.5 rounded ${glow(p.id)}`}>
          <button type="button" onClick={() => onOpen(p.id)} className="text-gray-800 dark:text-gray-200 hover:underline text-left">
            {p.title}
          </button>
          <span className="text-[11px] text-gray-400 whitespace-nowrap">
            {p.how} · {p.evidence} evidence
          </span>
          {p.verdict && <VerdictBadge verdict={p.verdict} />}
        </li>
      ))}
    </ul>
  );
}

/** How sure the board is about who makes a product, beside the maker's name.
 *  Says nothing for an older answer that carries no level. */
function MakerSureness({ maker, onOpen }: { maker: BoardroomMaker; onOpen: (id: number) => void }) {
  if (!maker.level) return null;
  const words =
    maker.level === 'confirmed'
      ? maker.own_site
        ? 'confirmed by its own site'
        : `confirmed by ${maker.sites?.length ?? 2} sites`
      : maker.level === 'one source'
        ? `one source: ${maker.sites?.[0] ?? 'one site'}`
        : 'unconfirmed';
  return (
    <span className="text-gray-500 dark:text-gray-400" data-testid="maker-sureness">
      {' '}({words})
      {(maker.also ?? []).length > 0 && (
        <span className="text-amber-700 dark:text-amber-300">
          {' '}· also claimed:{' '}
          {maker.also!.map((c, i) => (
            <span key={c.id}>
              {i > 0 && ', '}
              <button type="button" onClick={() => onOpen(c.id)} className="hover:underline">
                {c.title}
              </button>{' '}
              ({c.level})
            </span>
          ))}
        </span>
      )}
    </span>
  );
}

function ProductCard({
  product,
  onOpen,
  standalone = false,
  companyId,
  loadFilings,
}: {
  product: BoardroomProfileProduct;
  onOpen: (id: number) => void;
  standalone?: boolean;
  /** The company whose page this card is on: its filing is listed first. */
  companyId?: number;
  loadFilings?: (itemId: number) => Promise<BoardroomFilings>;
}) {
  const glow = useFreshClass();
  return (
    <article className={`shrink-0 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 p-3 ${standalone ? '' : glow(product.id)}`} data-testid={`profile-product-${product.id}`}>
      {!standalone && (
        <div className="flex items-baseline gap-2 flex-wrap">
          <button type="button" onClick={() => onOpen(product.id)} className="text-sm font-semibold text-gray-800 dark:text-gray-100 hover:underline text-left">
            {product.title}
          </button>
          {product.verdict && <VerdictBadge verdict={product.verdict} />}
          {product.solution_type && (
            <button type="button" onClick={() => onOpen(product.solution_type!.id)} className="text-xs text-violet-700 dark:text-violet-300 hover:underline">
              {product.solution_type.title}
            </button>
          )}
          {product.url ? (
            <a href={product.url} target="_blank" rel="noreferrer" className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline">
              site
            </a>
          ) : (
            <span className="text-xs text-gray-400 italic">no site</span>
          )}
          {!product.read && <span className="text-[11px] text-gray-400 italic">pages not read yet</span>}
          {loadFilings && <WhyHere itemId={product.id} focusParentId={companyId} load={loadFilings} onOpen={onOpen} />}
        </div>
      )}
      {!standalone && product.body && (
        <p className="mt-1 text-xs text-gray-600 dark:text-gray-300 line-clamp-2">
          <ItemRefText text={product.body} onOpen={onOpen} />
        </p>
      )}
      <div className={`${standalone ? '' : 'mt-2 '}grid gap-3 sm:grid-cols-3 text-xs`}>
        <div>
          <h4 className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">Features</h4>
          {product.features.length === 0 && <span className="text-gray-400 italic">none recorded</span>}
          <ul className="flex flex-wrap gap-1">
            {product.features.map(f => (
              <li key={f.id}>
                <button
                  type="button"
                  onClick={() => onOpen(f.id)}
                  title={f.plan ? `in the ${f.plan} plan` : undefined}
                  className={`px-1.5 py-0.5 rounded-full border border-cyan-200 dark:border-cyan-800 text-cyan-800 dark:text-cyan-300 text-xs hover:bg-cyan-50 dark:hover:bg-cyan-900/20 ${glow(f.id)}`}
                >
                  {f.title}
                  {f.plan && <span className="text-gray-400"> · {f.plan}</span>}
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">Pricing</h4>
          {product.plans.length === 0 && <span className="text-gray-400 italic">none recorded</span>}
          <ul className="flex flex-col gap-1">
            {product.plans.map(pl => (
              <li key={pl.id}>
                <button type="button" onClick={() => onOpen(pl.id)} className={`text-left hover:underline rounded ${glow(pl.id)}`}>
                  <span className="text-gray-800 dark:text-gray-200">{pl.title}</span>{' '}
                  <span className="text-orange-700 dark:text-orange-300">{pl.label}</span>
                  {pl.observed_at && <span className="text-[11px] text-gray-400"> · seen {pl.observed_at}</span>}
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">Pains</h4>
          <Pains pains={product.pains} onOpen={onOpen} />
          <Critiques rows={product.critique_rows} onOpen={onOpen} className="block mt-1" />
        </div>
      </div>
    </article>
  );
}

export default function ProfileView({
  data,
  watchControl,
  brandControl,
  onOpen,
  onPickTag,
  lensTags,
  onEdit,
  onDig,
  digging,
  unseen,
  onSeen,
  personas,
  loadFilings,
  onRuleOutVideo,
  onMerge,
  onKeepApart,
  lastDig,
  onCompare,
}: {
  /** "Compare with…" on a company's page. */
  onCompare?: () => void;
  /** The last dig aimed at this item, under its name (`LastDigSummary`). A
   *  slot, like `watchControl`: it fetches, and this file stays pure. */
  lastDig?: ReactNode;
  /** On a feature's page: fold the ticked near-copies into this feature. */
  onMerge?: (otherIds: number[]) => Promise<void>;
  /** On a feature's page: the ticked near-copies are different things. */
  onKeepApart?: (otherIds: number[]) => Promise<void>;
  /** "Not this one" on a video: rules it out, and it leaves the page. */
  onRuleOutVideo?: (videoId: number) => void;
  data: BoardroomProfile;
  /** The "watch this company" control. See `Head` for why it is a slot and not an import. */
  watchControl?: ReactNode;
  /** Who is going after this company's searches — a slot for the same reason. */
  brandControl?: ReactNode;
  /** Open another item: a profile for companies/products/types, the drawer for the rest. */
  onOpen: (itemId: number) => void;
  /** A tag on the head clicked: put it in the lens, or take it out. */
  onPickTag?: (key: string) => void;
  /** The tags in the lens now. */
  lensTags?: readonly string[];
  /** The drawer for this item, for tags, verdicts and edits. */
  onEdit: (itemId: number) => void;
  onDig: OnDig;
  digging: boolean;
  /** Ids that changed and have not been looked at. */
  unseen?: ReadonlySet<number>;
  /** Ids now on screen — the caller drops them from the badge. */
  onSeen?: (ids: number[]) => void;
  /** For "found by <name>" on the head. */
  personas?: Map<number, BoardroomPersona>;
  /** "Why is this here?" on each product: what it is filed under, and who filed it. */
  loadFilings?: (itemId: number) => Promise<BoardroomFilings>;
}) {
  const head = data.item;
  const isProduct = 'siblings' in data;
  const isFeature = 'shippers' in data;
  // WHAT JUST LANDED glows for a moment (see `freshness`): the page reloads as
  // a dig adds to it, and nothing said which rows were new.
  const fresh = useArrivals(head.id, data);
  const glow = (id: number) => `${GLOW_BASE} ${fresh.has(id) ? FRESH_CLASS : ''} rounded`;
  return (
    <FreshIds.Provider value={fresh}>
    <div className="flex flex-col gap-3 min-h-0 overflow-y-auto pr-1" data-testid="profile">
      <Head
        item={head}
        madeBy={isProduct ? { company: (data.item as BoardroomProfileProduct).company, siblings: data.siblings } : null}
        watchControl={watchControl}
        onCompare={onCompare}
        onEdit={() => onEdit(head.id)}
        onOpen={onOpen}
        onPickTag={onPickTag}
        lensTags={lensTags}
        personas={personas}
        // ONE DIG, WHERE IT IS SEEN. The per-gap buttons are at the foot of a
        // long page; someone standing on a company wants to send a persona at
        // THIS company without reading to the end to learn they can.
        dig={data.dig ?? null}
        onDig={onDig}
        digging={digging}
      />
      {lastDig}
      <MergedNames names={'merged_in' in data ? data.merged_in : undefined} />
      {/* A PRODUCT'S OWN PAGE says what it is filed under, too: right under
          its name, because that is what it is a claim about. */}
      {isProduct && loadFilings && (
        <div className="-mt-2 pl-1">
          <WhyHere itemId={head.id} load={loadFilings} onOpen={onOpen} />
        </div>
      )}
      {/* THE FACTS COME FIRST, right under the name, for a company and a
          product alike: what it is and who is behind it is what anyone
          opening it wants to know, and further down they were a long scroll
          away. */}
      {(head.kind === 'COMPANY' || head.kind === 'PRODUCT') && <Facts claims={head.claims ?? []} />}
      {/* WHO IS GOING AFTER ITS SEARCHES, at the top: further down, below a
          company's products and their features, nobody found it. */}
      {brandControl}
      {isFeature && <FeatureProfile data={data} onOpen={onOpen} onMerge={onMerge} onKeepApart={onKeepApart} />}
      {isProduct && (
        <>
          <ProductCard product={data.item as BoardroomProfileProduct} onOpen={onOpen} standalone />

        </>
      )}
      {'products' in data && (
        <section className="flex flex-col gap-2">
          <h3 className="shrink-0 text-xs uppercase tracking-wide text-gray-400 px-1">
            {'totals' in data
              ? `${data.totals.products} product${data.totals.products === 1 ? '' : 's'} · ${data.totals.features} feature${data.totals.features === 1 ? '' : 's'} · ${data.totals.priced} priced`
              : `Products · ${data.products.length}`}
          </h3>
          {data.products.length === 0 && <span className="text-xs text-gray-400 italic px-1">none recorded yet</span>}
          {data.products.map(p => (
            <ProductCard key={p.id} product={p} onOpen={onOpen} companyId={head.id} loadFilings={loadFilings} />
          ))}
        </section>
      )}
      {'also_claimed' in data && (data.also_claimed?.length ?? 0) > 0 && (
        <section className="shrink-0 rounded-xl border border-amber-200 bg-amber-50/60 p-3 text-xs dark:border-amber-800 dark:bg-amber-900/10" data-testid="profile-also-claimed">
          <h3 className="mb-1 text-[11px] uppercase tracking-wide text-amber-800 dark:text-amber-300">
            Filed here, but better supported as another company's
          </h3>
          <ul className="flex flex-col gap-0.5">
            {data.also_claimed!.map(p => (
              <li key={p.id} className={glow(p.id)}>
                <button type="button" onClick={() => onOpen(p.id)} className="hover:underline">
                  {p.title}
                </button>
                {p.maker && (
                  <span className="text-gray-500 dark:text-gray-400">
                    {' '}· made by{' '}
                    <button type="button" onClick={() => onOpen(p.maker!.id)} className="hover:underline">
                      {p.maker.title}
                    </button>
                    {p.maker.level && ` (${p.maker.level})`}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
      {/* WHAT A DEEP DIVE KEEPS, for the two kinds a deep dive is about: facts
          with their sources, other pages, and what people say both ways. */}
      {(head.kind === 'COMPANY' || head.kind === 'PRODUCT') && (
        <>
          {'images' in data && data.images && data.images.length > 0 && (
            <SiteImages images={data.images} name={head.title} />
          )}
          {'videos' in data && data.videos && data.videos.length > 0 && (
            <Videos videos={data.videos} name={head.title} onRuleOut={onRuleOutVideo} />
          )}
          <Links links={head.links ?? []} />
          {'voices' in data && data.voices && (
            <Voices voices={data.voices} itemId={head.id} onOpen={onOpen} />
          )}
        </>
      )}
      {'rankings' in data && data.rankings && data.rankings.length > 0 && (
        <section className="shrink-0 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 p-3" data-testid="profile-rankings">
          <h3 className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">Searches {head.title} ranks for</h3>
          <RankingsTable rankings={data.rankings} />
        </section>
      )}
      {data.demand.length > 0 && (
        <section className="shrink-0 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 p-3" data-testid="profile-demand">
          <h3 className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">
            What buyers search for · {data.demand.filter(t => t.seeded).length} term{data.demand.filter(t => t.seeded).length === 1 ? '' : 's'} asked about {head.title}
          </h3>
          {/* Its own memory: a profile shows a different set of columns from the
              report, so it remembers a different choice of them. */}
          <DemandTermsTable terms={data.demand} onOpen={onOpen} showAbout={false} table="profile-demand" />
        </section>
      )}
      {'pains' in data && !isProduct && !isFeature && (
        <section className="shrink-0 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 p-3">
          <h3 className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">Complaints about {head.title}</h3>
          <Pains pains={data.pains} onOpen={onOpen} />
        </section>
      )}
      {'ideas' in data && !isFeature && data.ideas.length > 0 && (
        <section className="shrink-0 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 p-3">
          <h3 className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">Ideas of this kind</h3>
          <ul className="flex flex-wrap gap-1">
            {data.ideas.map(i => (
              <li key={i.id} className={glow(i.id)}>
                <button type="button" onClick={() => onOpen(i.id)} className="px-1.5 py-0.5 rounded-full border border-indigo-200 dark:border-indigo-800 text-indigo-700 dark:text-indigo-300 text-xs hover:bg-indigo-50 dark:hover:bg-indigo-900/20">
                  {i.title}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
      {'children' in data && head.kind === 'QUESTION' && 'answer' in data && (
        <QuestionAnswer answer={data.answer ?? null} versions={data.answer_versions ?? []} onOpen={onOpen} />
      )}
      {'children' in data && data.links && (
        <ItemLinks links={data.links} lastDig={data.last_dig ?? null} onOpen={onOpen} />
      )}
      {/* EVERYTHING ELSE LINKED TO A FEATURE, with what the last dig at it
          added marked new: a dig aimed at a feature files most of what it
          finds (complaints, evidence, rewordings) as plain links, and the
          sections above have no place for them. */}
      {isFeature && data.links && (
        <ItemLinks links={data.links} lastDig={data.last_dig ?? null} onOpen={onOpen} />
      )}
      {'children' in data && !data.links && (
        <section className="shrink-0 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 p-3 text-xs">
          {Object.entries(data.children).map(([lk, rows]) => (
            <div key={lk} className="mb-2">
              <h4 className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">{lk.toLowerCase().replace(/_/g, ' ')}</h4>
              <ul className="flex flex-wrap gap-1">
                {rows.map(r => (
                  <li key={r.id}>
                    <button type="button" onClick={() => onOpen(r.id)} className="hover:underline">
                      {KIND_LABELS[r.kind]} · {r.title}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {Object.keys(data.children).length === 0 && <span className="text-gray-400 italic">nothing hangs under this yet</span>}
        </section>
      )}
      <MissingList missing={data.missing} empty={false} onOpen={onOpen} onDig={onDig} digging={digging} unseen={unseen} onSeen={onSeen} />
    </div>
    </FreshIds.Provider>
  );
}

/** Everything linked to an item, both ways, dated, newest first, with what
 *  the latest dig at it added marked. The verdict on top is the one that
 *  stands; the rest are kept as its history. */
/** ITS OLD NAMES: what was merged into it, so a merge does not lose what it
 *  used to be called. Plain text - a merged item has no page of its own. */
export function MergedNames({ names }: { names?: BoardroomMergedName[] }) {
  if (!names?.length) return null;
  return (
    <p className="-mt-2 shrink-0 px-1 text-xs text-gray-500 dark:text-gray-400" data-testid="merged-names">
      <span className="text-gray-400">Also seen as: </span>
      {names.map((n, i) => (
        <span key={n.id} title={n.at ? `merged in ${feedTimeTitle(n.at)}` : undefined}>
          {i > 0 && ', '}
          {n.title}
        </span>
      ))}
    </p>
  );
}

function ItemLinks({
  links,
  lastDig,
  onOpen,
}: {
  links: BoardroomItemLinkRow[];
  lastDig: { started: string; added: number } | null;
  onOpen: (id: number) => void;
}) {
  const groups = linkGroups(links);
  const glow = useFreshClass();
  return (
    <section
      className="shrink-0 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 p-3 text-xs"
      data-testid="item-links"
    >
      {lastDig && (
        <p className="mb-2 text-xs text-gray-500 dark:text-gray-400" data-testid="item-last-dig">
          Last dig into this: <span title={feedTimeTitle(lastDig.started)}>{feedTime(lastDig.started)}</span>
          {lastDig.added
            ? ` - added ${lastDig.added} link${lastDig.added === 1 ? '' : 's'}, marked new below.`
            : ' - it added nothing here.'}
        </p>
      )}
      {groups.length === 0 && <span className="text-gray-400 italic">nothing hangs under this yet</span>}
      {groups.map(g => (
        <div key={g.key} className="mb-2" data-testid={`item-links-${g.key}`}>
          <h4 className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">
            {g.heading} <span className="normal-case tracking-normal">· {g.rows.length}</span>
          </h4>
          <ul className="space-y-0.5">
            {g.rows.map((r, i) => (
              <li key={`${r.id}-${i}`} className={`flex items-baseline gap-2 rounded ${glow(r.id)}`}>
                {r.new && (
                  <span className="shrink-0 rounded bg-amber-100 px-1 text-[11px] text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                    new
                  </span>
                )}
                <button type="button" onClick={() => onOpen(r.id)} className="min-w-0 text-left hover:underline">
                  {KIND_LABELS[r.kind]} · {r.title}
                </button>
                {/* THE TOOL'S OWN PAGE is where its praise and complaints
                    are, side by side. */}
                {g.key === 'in:ABOUT' && (
                  <button
                    type="button"
                    onClick={() => onOpen(r.id)}
                    className="shrink-0 text-xs text-indigo-600 hover:underline dark:text-indigo-300"
                    data-testid={`item-about-feedback-${r.id}`}
                  >
                    see everything people say about {r.title} →
                  </button>
                )}
                {g.key === 'out:VERIFIES' && i === 0 && (
                  <span className="shrink-0 rounded bg-indigo-50 px-1 text-[11px] text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-200">
                    current
                  </span>
                )}
                <span className="ml-auto shrink-0 tabular-nums text-[11px] text-gray-400" title={feedTimeTitle(r.at)}>
                  {feedTime(r.at)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </section>
  );
}
